/**
 * O avatar do USUÁRIO do sistema — 9.1 · `telas/9.1-tarefas-kanban.html:173` · 2.3.
 *
 * No card do Kanban o responsável era o nome inteiro escrito por extenso, dividindo a
 * última linha com a data e uma borda. O protótipo põe ali um círculo com as iniciais,
 * alinhado à direita, e o nome no `title` — o card ganha a linha de volta e continua
 * dizendo de quem é a tarefa.
 *
 * **Sobre `iniciais()`.** A convenção é **primeira e última palavra**, que é como um nome
 * de pessoa se abrevia em português ("Ana Maria Silva" → `AS`, não `AM`).
 *
 * ## A foto (PK-04 · DS-518)
 *
 * Com `anexoId`, o círculo desenha a foto da pessoa pelo endereço ESTÁVEL
 * (`/anexos/:id/avatar`), que o navegador guarda em cache: um Kanban com quarenta cartões
 * de três pessoas pede três fotos, uma vez. As iniciais ficam por baixo até a foto
 * carregar, e voltam se ela falhar (404, rede) — **nunca o círculo vazio**. Sem `anexoId`,
 * são só as iniciais, como sempre foram.
 *
 * ## Quem usa, depois do PÓS-V1-004
 *
 * O pedido do fundador foi remover o círculo de iniciais das EMPRESAS e das pessoas
 * representadas apenas como contato, colaborador, fornecedor ou parceiro — mantendo o dos
 * **usuários do sistema**, "pois é possível acrescentar foto". Por isso este componente
 * só recebe usuário interno, e nada de empresa, contato ou portal passa por ele.
 */
import { useState } from 'react'
import { urlDoAvatar } from '@dv/contracts'
import { Icone } from '../shell/icone.tsx'

/** Iniciais de um nome: primeira e última palavra. Nome vazio vira `?`, nunca ``. */
export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((parte) => parte.length > 0)
  const primeira = partes[0]
  if (primeira === undefined) return '?'
  const ultima = partes.length > 1 ? partes[partes.length - 1] : undefined
  return ((primeira[0] ?? '') + (ultima?.[0] ?? '')).toUpperCase()
}

/**
 * Os tamanhos que as telas JÁ desenhavam, cada um com o nome de onde mora — transportados
 * como estavam, para a foto entrar sem mover um pixel do círculo de iniciais.
 */
export type TamanhoDoAvatar = 'cartao' | 'painel' | 'fio' | 'barra' | 'inbox' | 'perfil'

const CLASSE_DO_TAMANHO: Readonly<Record<TamanhoDoAvatar, string>> = {
  cartao: 'h-6 w-6 text-[10px]', // Kanban, listas, seletor de menção
  painel: 'h-7 w-7 text-[11px]', // eixo "Por executivo" da 3.8
  fio: 'h-8 w-8 text-[11px]', // autor do comentário
  barra: 'h-8 w-8 text-[12px]', // rodapé da barra lateral
  inbox: 'h-9 w-9 text-[11px]', // autor da notificação
  perfil: 'h-20 w-20 text-[22px]', // o próprio Perfil (2.3)
}

const ICONE_DO_TAMANHO: Readonly<Record<TamanhoDoAvatar, string>> = {
  cartao: 'h-3.5 w-3.5',
  painel: 'h-4 w-4',
  fio: 'h-4 w-4',
  barra: 'h-4 w-4',
  inbox: 'h-4.5 w-4.5',
  perfil: 'h-9 w-9',
}

/** navy = o comum · suave = painel da 3.8 · ceu = fio de comentários · escuro = barra lateral. */
export type TomDoAvatar = 'navy' | 'suave' | 'ceu' | 'escuro'

function classeDoTom(tom: TomDoAvatar, tamanho: TamanhoDoAvatar): string {
  if (tom === 'escuro') return 'bg-white/10 text-white'
  if (tom === 'ceu') return 'bg-sky-50 text-sky-800'
  if (tom === 'suave') return 'bg-navy/5 text-navy'
  return tamanho === 'perfil' ? 'bg-navy/[.08] text-navy' : 'bg-navy/10 text-navy'
}

export function Avatar({
  nome,
  anexoId = null,
  src = null,
  tamanho = 'cartao',
  tom = 'navy',
  semResponsavel = false,
  decorativo = false,
  teste = 'avatar',
  className = '',
}: {
  nome: string
  /** O anexo da foto (`avatarAnexoId`); `null` desenha só as iniciais. */
  anexoId?: string | null
  /** Endereço que vence o `anexoId` — a prévia local do Perfil enquanto a foto sobe. */
  src?: string | null
  tamanho?: TamanhoDoAvatar
  tom?: TomDoAvatar
  /** Ninguém responde pelo item: um ícone neutro, e não as iniciais de "Sem responsável". */
  semResponsavel?: boolean
  /** O nome já está escrito ao lado: o círculo sai da árvore de acessibilidade, sem repetir o nome. */
  decorativo?: boolean
  /** O `data-teste` do círculo; a barra lateral usa o seu, para não se confundir com o da tela. */
  teste?: string
  className?: string
}) {
  const endereco = semResponsavel ? null : (src ?? (anexoId === null ? null : urlDoAvatar(anexoId)))
  // O endereço que já carregou e o que falhou. Guardar O ENDEREÇO, e não um booleano, faz a
  // troca de foto recomeçar do zero sem efeito nenhum: o estado antigo simplesmente não casa.
  const [carregada, setCarregada] = useState<string | null>(null)
  const [falhou, setFalhou] = useState<string | null>(null)
  const pronta = endereco !== null && carregada === endereco
  return (
    <span
      /*
        `title` e `sr-only` carregam o nome inteiro: o círculo é um resumo visual, e um
        resumo visual não pode ser a única forma de saber de quem é a tarefa.
      */
      title={decorativo ? undefined : nome}
      aria-hidden={decorativo ? true : undefined}
      data-teste={teste}
      data-foto={pronta ? 'sim' : 'nao'}
      /*
        `relative` não é enfeite: sem ele, o `sr-only` de dentro — que é
        `position:absolute` — não tem ancestral posicionado, ancora no bloco inicial da
        página e ESCAPA do contêiner que rola. No quadro do Kanban, que é largo e rola
        por dentro, isso empurrava a largura da PÁGINA de 412 para 496px no celular: a
        tela inteira passava a deslizar para o lado. O sintoma não aponta para a causa —
        o que se vê é um clique interceptado noutro diálogo.
      */
      className={`relative grid shrink-0 place-items-center overflow-hidden rounded-full font-semibold ${CLASSE_DO_TAMANHO[tamanho]} ${classeDoTom(tom, tamanho)} ${className}`}
    >
      {semResponsavel ? (
        <Icone nome="user-round" className={`${ICONE_DO_TAMANHO[tamanho]} opacity-60`} />
      ) : pronta ? null : (
        <span aria-hidden="true">{iniciaisDe(nome)}</span>
      )}
      {endereco !== null && falhou !== endereco ? (
        <img
          src={endereco}
          alt=""
          draggable={false}
          decoding="async"
          data-teste="avatar-foto"
          className={`absolute inset-0 h-full w-full object-cover ${pronta ? '' : 'opacity-0'}`}
          onLoad={() => {
            setCarregada(endereco)
          }}
          onError={() => {
            setFalhou(endereco)
          }}
        />
      ) : null}
      {decorativo ? null : <span className="sr-only">{nome}</span>}
    </span>
  )
}
